// A small, self-contained analogue of the configuration engine I built at HSBC.
// Confidential insurance data is replaced with a trip specification: trip fields
// mirror product-specification fields, packing items mirror UI components, and
// configurator columns mirror business scenarios in the production decision tables.
//
// Like the real tool, the logic is split into several independent configurators.
// Conditional follow-up fields, the ALL wildcard, and right-most-specific rule
// priority mirror the production mapping logic.

// A field with a `parent` is a follow-up: it only appears when the parent holds
// one of the `when` values (mirrors the real "2.2.1(Para1).1" S.No syntax).
export const fields = [
  { sno: 'TRIP', name: 'Trip type', params: ['Beach', 'Ski', 'City break'], default: 'Beach' },
  { sno: 'TRIP(Beach).1', name: 'Doing water sports?', parent: 'TRIP', when: ['Beach'], params: ['No', 'Yes'], default: 'No' },
  { sno: 'TRIP(Ski).1', name: 'Renting gear there?', parent: 'TRIP', when: ['Ski'], params: ['No', 'Yes'], default: 'No' },
  { sno: 'DEST', name: 'Destination', params: ['Domestic', 'International'], default: 'Domestic' },
  { sno: 'LEN', name: 'Trip length', params: ['Short break', 'Long trip'], default: 'Short break' },
];

// ── Items: everything you could pack ─────────────────────────────────────────
export const components = [
  { id: 'SWIM', name: 'Swimwear & sunscreen', hint: 'For the beach' },
  { id: 'SNORKEL', name: 'Snorkel & fins', hint: 'For the water' },
  { id: 'SKIWEAR', name: 'Warm layers & ski gear', hint: 'Thermals, jacket, gloves' },
  { id: 'SHOES', name: 'Comfy walking shoes', hint: 'For exploring on foot' },
  { id: 'PASSPORT', name: 'Passport & travel adapter', hint: 'Crossing a border' },
  { id: 'LAUNDRY', name: 'Laundry & toiletries kit', hint: 'For a longer stay' },
];

// ── Configurators: each is a small rule table over its own questions and items.
// Columns (scenarios) go general → specific. A blank condition means "ALL"
// (matches anything). For each table the right-most matching column wins and its
// Y/N row decides those items. The packing list is the union across all tables.
export const configurators = [
  {
    id: 'gear',
    name: 'Activity gear',
    blurb: 'What you actually do on the trip',
    inputs: ['TRIP', 'TRIP(Beach).1', 'TRIP(Ski).1'],
    items: ['SWIM', 'SNORKEL', 'SKIWEAR', 'SHOES'],
    scenarios: [
      { name: 'Any trip',            cond: {},                                          out: { SWIM: 'N', SNORKEL: 'N', SKIWEAR: 'N', SHOES: 'N' } },
      { name: 'Beach',               cond: { TRIP: 'Beach' },                           out: { SWIM: 'Y', SNORKEL: 'N', SKIWEAR: 'N', SHOES: 'N' } },
      { name: 'Beach · water sports', cond: { TRIP: 'Beach', 'TRIP(Beach).1': 'Yes' }, out: { SWIM: 'Y', SNORKEL: 'Y', SKIWEAR: 'N', SHOES: 'N' } },
      { name: 'Ski',                 cond: { TRIP: 'Ski' },                             out: { SWIM: 'N', SNORKEL: 'N', SKIWEAR: 'Y', SHOES: 'N' } },
      { name: 'Ski · renting',       cond: { TRIP: 'Ski', 'TRIP(Ski).1': 'Yes' },       out: { SWIM: 'N', SNORKEL: 'N', SKIWEAR: 'N', SHOES: 'N' } },
      { name: 'City break',          cond: { TRIP: 'City break' },                      out: { SWIM: 'N', SNORKEL: 'N', SKIWEAR: 'N', SHOES: 'Y' } },
    ],
  },
  {
    id: 'docs',
    name: 'Travel documents',
    blurb: 'Whether you cross a border',
    inputs: ['DEST'],
    items: ['PASSPORT'],
    scenarios: [
      { name: 'Domestic',      cond: {},                        out: { PASSPORT: 'N' } },
      { name: 'International',  cond: { DEST: 'International' },  out: { PASSPORT: 'Y' } },
    ],
  },
  {
    id: 'stay',
    name: 'Length of stay',
    blurb: 'How long you are away',
    inputs: ['LEN'],
    items: ['LAUNDRY'],
    scenarios: [
      { name: 'Short break',  cond: {},                     out: { LAUNDRY: 'N' } },
      { name: 'Long trip',    cond: { LEN: 'Long trip' },   out: { LAUNDRY: 'Y' } },
    ],
  },
];

// Which questions are currently on screen, given the answers (follow-up logic).
export function visibleFields(answers) {
  return fields.filter(f => !f.parent || (f.when || []).includes(answers[f.parent]));
}

// Evaluate one configurator's table: every condition must match (ALL is a
// wildcard), then the right-most matching column wins.
function evalConfigurator(cfg, answers, visible) {
  const results = cfg.scenarios.map(s => {
    let match = true;
    const cell = {};
    for (const sno of cfg.inputs) {
      const req = Object.prototype.hasOwnProperty.call(s.cond, sno) ? s.cond[sno] : 'ALL';
      const ok = req === 'ALL' ? true : (visible.has(sno) && answers[sno] === req);
      cell[sno] = { req, ok };
      if (!ok) match = false;
    }
    return { match, cell };
  });
  let winning = -1;
  results.forEach((r, i) => { if (r.match) winning = i; });
  const out = winning >= 0 ? cfg.scenarios[winning].out : null;
  return { results, winning, out };
}

// Evaluate every configurator and union their "Y" items into one packing list.
export function evaluate(answers) {
  const visible = new Set(visibleFields(answers).map(f => f.sno));
  const groups = configurators.map(cfg => ({ cfg, ...evalConfigurator(cfg, answers, visible) }));

  const onIds = new Set();
  groups.forEach(g => {
    if (g.out) g.cfg.items.forEach(id => { if (g.out[id] === 'Y') onIds.add(id); });
  });
  const shown = components.filter(c => onIds.has(c.id));
  const out = {};
  components.forEach(c => { out[c.id] = onIds.has(c.id) ? 'Y' : 'N'; });

  return { groups, visible, shown, out };
}

// Field lookup by S.No (for rendering a configurator's input rows).
export function fieldBySno(sno) {
  return fields.find(f => f.sno === sno);
}
// Item lookup by id.
export function componentById(id) {
  return components.find(c => c.id === id);
}

// The default set of answers (every field on its default value).
export function defaultAnswers() {
  const a = {};
  for (const f of fields) a[f.sno] = f.default;
  return a;
}
